import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, IconTile, InstallPrompt, ListRow, Money, Screen, SectionLabel, Spinner, Stack } from '../components';
import { ChevronRightIcon, LogOutIcon, PlusIcon, UsersIcon } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { useAsync } from '../hooks/useAsync';
import { listTrips } from '../data/trips';
import { formatDate } from '../lib/dates';

/** The landing screen: every trip you are on, and how you stand on each. */
export function TripsPage() {
  const { user, signOut } = useAuth();
  const loader = useCallback(() => listTrips(), []);
  const { data, loading, error } = useAsync(loader, user?.id ?? 'anonymous');

  return (
    <Screen maxWidth={560}>
      <Stack gap={5}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--fs-display)', letterSpacing: '-0.02em', fontWeight: 300 }}>
              Tabi
            </h1>
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-md)' }}>
              {user?.email}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            <LogOutIcon size={16} />
            Sign out
          </Button>
        </header>

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <Link to="/trips/new" style={{ flex: 1 }}>
            <Button fullWidth size="lg">
              <PlusIcon size={18} />
              New trip
            </Button>
          </Link>
          <Link to="/join" style={{ flex: 1 }}>
            <Button fullWidth size="lg" variant="secondary">
              Join with a code
            </Button>
          </Link>
        </div>

        <InstallPrompt />

        {error && <Alert tone="error">{error}</Alert>}

        {loading && !data ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-10)' }}>
            <Spinner size={24} label="Loading your trips" />
          </div>
        ) : !data || data.length === 0 ? (
          <Card>
            <EmptyState title="No trips yet">
              Create one and share the invite code, or join a trip somebody has already set up.
            </EmptyState>
          </Card>
        ) : (
          <div>
            <SectionLabel>Your trips</SectionLabel>
            <Card flush>
              {data.map(({ trip, netBase, memberCount }, index) => (
                <ListRow key={trip.id} divider={index > 0}>
                  <Link
                    to={`/trips/${trip.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                      flex: 1,
                      minWidth: 0,
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    <IconTile size={42}>
                      <UsersIcon size={20} />
                    </IconTile>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 'var(--fs-body-lg)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {trip.name}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
                        {memberCount} {memberCount === 1 ? 'person' : 'people'} ·{' '}
                        {trip.starts_on ? formatDate(trip.starts_on) : trip.base_currency}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Money amount={netBase} currency={trip.base_currency} tone="auto" signed />
                      <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
                        {netBase < -0.005 ? 'you owe' : netBase > 0.005 ? 'you are owed' : 'settled'}
                      </div>
                    </div>
                    <ChevronRightIcon size={16} style={{ color: 'var(--text-muted)' }} />
                  </Link>
                </ListRow>
              ))}
            </Card>
          </div>
        )}
      </Stack>
    </Screen>
  );
}
